import "preact/debug";
import { render } from "preact";
import { useState, useEffect } from "preact/hooks";
import { LocationProvider, ErrorBoundary } from "preact-iso";
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
  matchQuery
} from "@tanstack/preact-query";
import { AuthMiddleware, LoginExpiredError } from "@doors/api-client";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { fetchClient } from "./data/api-client";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthContext, loadUser, onLogout } from "./data/auth";
import type { UserCreds } from "./data/auth";
import { LoginOrApp } from "./LoginRegister";
import { ThemeProvider } from "@mui/material";
import { theme } from "./theme";

import "@fontsource/oswald/500.css";
import "@fontsource/oswald/600.css";
import "@fontsource/oswald/700.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";

export const RETRY_COUNT = 3;

// from https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations
// after mutation, invalidate queries with matching keys or all queries
// if the key is not provided
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (errCount: number, err: Error) => {
        if (err instanceof LoginExpiredError) {
          console.log("Saw an invalid JWT, logging out");
          onLogout();
        }
        return errCount < RETRY_COUNT;
      }
    }
  },
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-expect-error straight out of the tanstack docs, works fine
          mutation.meta?.invalidates?.some((queryKey) =>
            matchQuery({ queryKey }, query)
          ) ?? true
      });
    }
  })
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
  }
}
window.__TANSTACK_QUERY_CLIENT__ = queryClient;

function App() {
  const [user, setUser] = useState<UserCreds | null>(null);
  // provide the authenticated user details to the rest of the app
  useEffect(() => {
    async function user() {
      const user = await loadUser();
      if (user != null) {
        setUser(user);
      }
    }
    user();
  }, []);
  useEffect(() => {
    if (user != null) {
      fetchClient.use(AuthMiddleware(user.jwt));
    }
  }, [user]);
  return (
    <LocationProvider>
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CssBaseline />
            <AuthContext.Provider value={{ user, setUser }}>
              <QueryClientProvider client={queryClient}>
                <LoginOrApp />
              </QueryClientProvider>
            </AuthContext.Provider>
          </LocalizationProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </LocationProvider>
  );
}

render(<App />, document.getElementById("app")!);
